import { stableStringify } from '@provision/formatting-utils';
import type { Content, Editor, JSONContent } from '@provision/rich-text-editor';
import type { MutableRefObject } from 'react';
import type { AppliedMacroAction } from 'src/pages/Conversations/Chat/AiAssistant/ai-macro-draft-context';
import type { EditorDoc } from 'src/pages/Conversations/Chat/conversation-draft-macros';

/** The editor's own doc, in the exact shape `applyEditorContent` compares against. */
export function readEditorDocJson(ed: Editor | null): string {
  return JSON.stringify(ed?.getJSON?.() ?? null);
}

/** Hydrate: json-first (strip meta), fallback HTML, dedupe by comparing current editor content. */
export function applyEditorContent(
  ed: Editor | null,
  raw: string,
  lastApplied: MutableRefObject<string>,
): void {
  if (!ed || !raw) return;
  if (raw === lastApplied.current) return;

  try {
    const parsed = JSON.parse(raw) as EditorDoc;
    const target = stripMeta(parsed);
    const curJSON = readEditorDocJson(ed);
    const nextJSON = JSON.stringify(target);
    if (curJSON !== nextJSON) {
      ed.commands.setContent(target);
    }
    lastApplied.current = nextJSON;
    return;
  } catch {
    // not json - treat as HTML
  }

  const curHtml = (ed.getHTML() || '').trim();
  const nextHtml = (raw || '').trim();
  if (curHtml !== nextHtml) {
    ed.commands.setContent(nextHtml);
  }
  lastApplied.current = nextHtml;
}

/** Remove meta from a draft JSON before giving it to the editor. */
export function stripMeta(doc: JSONContent) {
  const { meta, ...rest } = doc || {};
  return rest as string & Content;
}

export function editorHasContent(ed: Editor | null): boolean {
  const text = ed?.getText?.() ?? '';
  return text.trim().length > 0;
}

// ProseMirror's base keymap claims Escape itself (selectParentNode), so `defaultPrevented` cannot
// tell an open menu apart; the slash and mention menus are tiptap suggestion plugins, which
// expose `active` in their plugin state.
export function hasOpenEditorSuggestion(editor: Editor | null): boolean {
  if (!editor) return false;
  return editor.state.plugins.some((plugin) => {
    const pluginState: unknown = plugin.getState(editor.state);
    return (
      typeof pluginState === 'object' &&
      pluginState !== null &&
      (pluginState as { active?: unknown }).active === true
    );
  });
}

export type EditorContentDecision = 'apply-draft' | 'hydrate-suggestion' | 'clear' | 'keep';

export interface EditorContentInputs {
  activeRawDraft: string;
  previousRawDraft: string | null;
  editorHasContent: boolean;
  editorHoldsAppliedContent: boolean;
  uneditedSuggestionInEditor: boolean;
  suggestionAvailable: boolean;
}

/** The only decision the composer's hydration effect makes; the effect just executes it. */
export function decideEditorContent({
  activeRawDraft,
  previousRawDraft,
  editorHasContent,
  editorHoldsAppliedContent,
  uneditedSuggestionInEditor,
  suggestionAvailable,
}: EditorContentInputs): EditorContentDecision {
  if (uneditedSuggestionInEditor) {
    return 'keep';
  }

  if (activeRawDraft) {
    return activeRawDraft === previousRawDraft ? 'keep' : 'apply-draft';
  }

  // an empty draft may take the editor back only from a draft this effect itself put there
  // order matters here
  if (!previousRawDraft) {
    return 'keep';
  }

  if (editorHasContent && !editorHoldsAppliedContent) {
    return 'keep';
  }

  return suggestionAvailable ? 'hydrate-suggestion' : 'clear';
}

/** Insert the macro text (replace an empty editor, append otherwise), then snapshot the draft. */
export function applyMacroContent(
  editor: Editor | null,
  html: string,
  draft: { macros: AppliedMacroAction[]; prev: EditorDoc | null },
): string {
  if (!editor || !editorHasContent(editor)) {
    editor?.commands.setContent(html);
  } else {
    const endPos = editor.state.doc.content.size;
    editor.chain().setTextSelection(endPos).insertContent(html).run();
  }

  return buildDraftJson(editor, draft.macros, draft.prev);
}

/** Build the stringified draft json: current editor doc + macros in meta (editor doc stays clean). */
export function buildDraftJson(
  editor: Editor | null,
  macros: AppliedMacroAction[],
  prev: EditorDoc | null,
): string {
  const cleanDoc = editor?.getJSON?.() ?? {};
  const prevMeta = prev?.meta ?? {};
  const merged = { ...cleanDoc, meta: { ...prevMeta, macros } };

  return stableStringify(merged);
}
